<template>
  <section class="webinar-section">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">Webinars</h2>
        <p class="section-subtitle">Learn from industry experts and franchise leaders</p>
      </div>
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="text-center py-5">
          <div class="loading-spinner"></div>
          <p>Loading webinars...</p>
        </div>
      </div>
      
      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="text-center py-5">
          <i class="fa fa-exclamation-triangle empty-icon"></i>
          <h4>Failed to load webinars</h4>
          <p>{{ error }}</p>
        </div>
      </div>
      
      <!-- Webinars Grid -->
      <div v-else-if="webinars.length > 0" class="webinar-grid">
        <div class="webinar-grid-inner">
          <div v-for="(webinar, idx) in webinars" :key="webinar.id || idx" class="webinar-card">
            <div class="webinar-thumbnail" @click="openModal(webinar)">
              <img :src="webinar.thumbnail || webinar.image" :alt="webinar.name || webinar.title" class="webinar-image" />
              <div class="webinar-play-overlay">
                <i class="fa fa-play-circle play-icon"></i>
              </div>
            </div>
            <div class="webinar-content">
              <div class="webinar-title-row">
                <h3 class="webinar-title">{{ webinar.name || webinar.title }}</h3>
                <span class="webinar-duration-pill">
                  <i class="fa fa-stopwatch duration-icon"></i>
                  {{ webinar.duration || '45 min' }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="text-center mt-5">
          <router-link to="/all-webinars" class="orange-btn">
            View All Webinars
          </router-link>
        </div>
      </div>
      
      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="text-center py-5">
          <i class="fa fa-video-camera empty-icon"></i>
          <h4>No webinars available</h4>
          <p>Check back soon for new webinars.</p>
        </div>
      </div>
    </div>

    <!-- Video Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-header-bar">
        <span class="modal-title">Webinar</span>
        <button class="modal-close" @click="closeModal">Close &times;</button>
      </div>
      <div class="modal-video-center">
        <div class="video-wrapper">
          <iframe
            v-if="modalVideoType === 'youtube'"
            :key="'youtube-' + modalKey"
            :src="youtubeEmbedUrl(modalVideo)"
            frameborder="0"
            allow="autoplay; encrypted-media"
            allowfullscreen
            class="modal-video"
          ></iframe>
          <iframe
            v-else-if="modalVideoType === 'vimeo'"
            :key="'vimeo-' + modalKey"
            :src="vimeoEmbedUrl(modalVideo)"
            frameborder="0"
            allow="autoplay; encrypted-media"
            allowfullscreen
            class="modal-video"
          ></iframe>
          <video
            v-else-if="modalVideoType === 'direct'"
            :key="'direct-' + modalKey"
            :src="modalVideo"
            controls
            autoplay
            class="modal-video"
          ></video>
          <div v-else class="text-center py-5" style="color: #fff;">Video format not supported.</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useWebinarStore } from '@/stores/webinarStore'

const webinarStore = useWebinarStore()
const webinars = ref([])
const loading = ref(false)
const error = ref(null)

const showModal = ref(false)
const modalVideo = ref('')
const modalVideoType = ref('')
const modalKey = ref(0)

// Fetch webinars on component mount
onMounted(async () => {
  try {
    loading.value = true
    error.value = null
    
    // Fetch webinars from store
    await webinarStore.fetchWebinars()
    
    // Check if store has error
    if (webinarStore.error) {
      error.value = webinarStore.error
    } else {
      // Get active webinars (limit to 6 for homepage)
      const activeWebinars = webinarStore.activeWebinars.slice(0, 6)
      webinars.value = activeWebinars
  
    }
  } catch (err) {
    error.value = 'Failed to load webinars'
  } finally {
    loading.value = false
  }
})

function getVideoType(url) {
  if (!url) return ''
  if (/youtube\.com|youtu\.be/.test(url)) return 'youtube'
  if (/vimeo\.com/.test(url)) return 'vimeo'
  if (/\.mp4$|\.webm$|\.ogg$/.test(url) || url.startsWith('http')) return 'direct'
  return ''
}

function openModal(webinar) {
  modalVideo.value = webinar.video_link || webinar.video
  modalVideoType.value = getVideoType(webinar.video_link || webinar.video)
  modalKey.value++
  showModal.value = true
}

function closeModal() {
  showModal.value = false
}

function youtubeEmbedUrl(url) {
  const match = url.match(/(?:youtube\.com\/.*[?&]v=|youtu\.be\/)([\w-]{11})/)
  const id = match ? match[1] : ''
  return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : ''
}

function vimeoEmbedUrl(url) {
  const match = url.match(/vimeo\.com\/(\d+)/)
  const id = match ? match[1] : ''
  return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : ''
}
</script>

<style scoped>
.webinar-section {
  padding: 80px 0;
  /* background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); */
  background: transparent;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 16px;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.08);
}

.section-subtitle {
  font-size: 1.1rem;
  color: #6c757d;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.webinar-grid {
  margin-bottom: 40px;
}

.webinar-grid-inner {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 40px;
}

.webinar-card {
  /* background: #fff; */
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(44, 62, 80, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: transparent;
}

.webinar-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 16px 48px rgba(44, 62, 80, 0.15);
}

.webinar-thumbnail {
  position: relative;
  overflow: hidden;
}

.webinar-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.webinar-card:hover .webinar-image {
  transform: scale(1.05);
}

.webinar-play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.3);
  transition: background 0.3s ease;
}

.webinar-card:hover .webinar-play-overlay {
  background: rgba(0,0,0,0.4);
}

.play-icon {
  font-size: 48px;
  color: #fff;
  opacity: 0.9;
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
  transition: transform 0.3s ease;
}

.webinar-card:hover .play-icon {
  transform: scale(1.1);
}

.webinar-content {
  padding: 24px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.webinar-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 0;
}

.webinar-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #23272f;
  margin: 0;
  line-height: 1.3;
  flex: 1 1 auto;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.webinar-duration-pill {
  display: flex;
  align-items: center;
  background: rgba(255, 107, 53, 0.15);
  color: #ff6b35;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 16px;
  padding: 4px 14px 4px 10px;
  margin-left: 8px;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(44,62,80,0.04);
  letter-spacing: 0.5px;
}

.duration-icon {
  font-size: 1.15em;
  margin-right: 7px;
  color: #ff6b35;
  opacity: 0.95;
  vertical-align: middle;
}

.empty-state,
.loading-state,
.error-state {
  padding: 60px 0;
}

.empty-icon {
  font-size: 4rem;
  color: #dee2e6;
  margin-bottom: 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #ff6b35;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Button Styles */
.orange-outline-btn {
  display: inline-block;
  padding: 12px 32px;
  background: transparent;
  border: 2px solid #ff6b35;
  color: #ff6b35;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.orange-outline-btn:hover {
  background: transparent;
  color: #ff6b35;
  border-color: #ff6b35;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.2);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.85);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.modal-header-bar {
  width: 100vw;
  max-width: 900px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #fff;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 8px;
  padding: 0 24px;
}

.modal-title {
  font-size: 1.2rem;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.modal-close {
  background: none;
  border: none;
  color: #fff;
  font-size: 2rem;
  cursor: pointer;
  margin-left: 16px;
  font-weight: 400;
}

.modal-video-center {
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
}

.video-wrapper {
  background: #000;
  border-radius: 20px;
  overflow: hidden;
  max-width: 900px;
  width: 90vw;
  box-shadow: 0 8px 32px rgba(44, 62, 80, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-video {
  width: 100%;
  height: 55vw;
  max-width: 900px;
  max-height: 60vh;
  border-radius: 20px;
  background: #000;
  display: block;
}

/* Responsive Design */
@media (max-width: 768px) {
  .webinar-section {
    padding: 60px 0;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .section-subtitle {
    font-size: 1rem;
  }
  
  .webinar-content {
    padding: 20px;
  }
  
  .webinar-title {
    font-size: 1rem;
  }
}

@media (max-width: 900px) {
  .webinar-grid-inner {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
  
  .modal-header-bar {
    max-width: 100vw;
    padding: 0 8px;
  }
  
  .video-wrapper {
    max-width: 100vw;
    border-radius: 12px;
  }
  
  .modal-video {
    border-radius: 12px;
    height: 50vw;
    max-height: 40vh;
  }
}

@media (max-width: 600px) {
  .webinar-section {
    padding: 40px 0;
  }
  
  .section-title {
    font-size: 1.8rem;
  }
  
  .modal-header-bar {
    font-size: 1rem;
    padding: 0 4px;
  }
  
  .modal-title {
    font-size: 1rem;
  }
  
  .modal-close {
    font-size: 1.5rem;
  }
  
  .video-wrapper {
    border-radius: 8px;
  }
  
  .modal-video {
    border-radius: 8px;
    height: 40vw;
    max-height: 30vh;
  }
  
  .webinar-grid-inner {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
</style> 
